import { BreakpointObserver } from '@angular/cdk/layout';
import { ChangeDetectorRef, Directive, EventEmitter, inject, Input, OnDestroy, OnInit, Output } from '@angular/core';
import { UntypedFormBuilder, UntypedFormGroup, Validators } from '@angular/forms';
import { Destroyable } from '@dlw-int-mwi/eps-common';
import { TranslateService } from '@ngx-translate/core';
import { CDTo } from 'app/core/store/contract-details/contract-details.interface';
import { Definition } from 'app/core/store/definitions/definition.interface';
import { Definitions } from 'app/core/store/definitions/definitions.interface';
import { DeliveryMethod } from 'app/core/store/edit-florder-detail/florder-detail.interface';
import { PanelStatus } from 'app/modules/florder/florder-edit-container/florder-edit.interface';
import { PoNumberAsyncValidator, RestCallParams, ValidationParams } from 'app/modules/florder/florder-edit-delivery/po-number-async-validator';
import { BehaviorSubject, combineLatest, Observable, Subscription } from 'rxjs';
import { debounceTime, distinctUntilChanged, first, map } from 'rxjs/operators';
import { LayoutBreakpoints } from '../../../core/util/layout-breakpoints';
import { util } from '../../../core/util/util';

@Directive()
export abstract class FlorderEditDeliveryComponent extends Destroyable implements OnInit, OnDestroy {
  private type: string;
  deliveryForm: UntypedFormGroup;
  shippingConditions: Definition[] = [];
  froms: Definition[] = [];
  tos: Definition[] = [];
  incoterms: Definition[] = [];
  poNumberMandatory: boolean = false;

  senderRefNrValidationWarningObject: { [key: string]: boolean };
  receiverRefNrValidationWarningObject: { [key: string]: boolean };
  senderRefNrValidationErrorObject: { [key: string]: boolean };
  receiverRefNrValidationErrorObject: { [key: string]: boolean } = null;
  bothRefNrsValidationPending = new BehaviorSubject<boolean>(false);
  senderRefNrValidationPending = new BehaviorSubject<boolean>(false);
  receiverRefNrValidationPending = new BehaviorSubject<boolean>(false);
  protected bothRefNrsValidated = false;
  refNrValidationPending = combineLatest([this.senderRefNrValidationPending, this.receiverRefNrValidationPending, this.bothRefNrsValidationPending], (receiverPending, senderPending, bothPending) => {
    return receiverPending || senderPending || bothPending;
  });
  poNumberMask: string = '';
  resolutionOkForArrow = true;

  @Input() deliveryMethod: DeliveryMethod;
  @Input() panelStatus: PanelStatus;
  @Input() definitions: Definitions;
  @Input() busy = false;

  @Output() nextClicked = new EventEmitter();
  @Output() cancelClicked = new EventEmitter();
  @Output() formValidChanged = new EventEmitter();

  private subscriptions: Subscription[] = [];
  private validationPerformed = false;

  private formBuilder = inject(UntypedFormBuilder);
  protected translate = inject(TranslateService);
  protected poNumberAsyncValidator = inject(PoNumberAsyncValidator);
  private breakpointObserver = inject(BreakpointObserver);
  protected cd = inject(ChangeDetectorRef);

  constructor() {
    super();
  }

  ngOnInit(): void {
    this.listenToLayoutBreakpoints();
    this.createForm();

    // If this component is not in edit mode, and validation not yet triggered
    if (!this.panelStatus.isEdit && !this.validationPerformed) {
      ['customerReferenceNumber', 'senderReferenceNumber'].forEach(prop => {
        this.deliveryForm.get(prop).updateValueAndValidity();
      });
      this.validationPerformed = true;
    }
  }

  private listenToLayoutBreakpoints() {
    LayoutBreakpoints
      .matches(this.breakpointObserver, LayoutBreakpoints.bootstrapSmOrMore)
      .pipe(this.takeUntilDestroyed())
      .subscribe(resolutionOkForArrow => {
        this.resolutionOkForArrow = resolutionOkForArrow;
        this.cd.markForCheck();
      });
  }

  createForm(): void {
    // Remember async validation errors
    const customerReferenceNumberErrors = this.deliveryForm && this.deliveryForm.get('customerReferenceNumber') && this.deliveryForm.get('customerReferenceNumber').errors;
    this.deliveryForm = this.formBuilder.group(this.getControlsConfig());
    if (customerReferenceNumberErrors) {
      this.deliveryForm.get('customerReferenceNumber').setErrors(customerReferenceNumberErrors);
    }
    this.formValidChanged.emit(this.deliveryForm.valid);
    this.deliveryForm.statusChanges
      .pipe(
        map(data => this.deliveryForm.valid),
        distinctUntilChanged()
      )
      .subscribe(valid => {
        this.formValidChanged.emit(valid);
      });
  }

  /**
   * Only for flows & orders, not for relocations, so it's not part of the this createForm function anymore
   */
  protected triggerValidatePONumber() {
    // eslint-disable-next-line
    const thiz = this;
    this.subscriptions.push(this.deliveryForm.get('customerReferenceNumber').valueChanges.subscribe(() => (thiz.receiverRefNrValidationPending.next(true))));
    this.subscriptions.push(this.deliveryForm.get('senderReferenceNumber').valueChanges.subscribe(() => (thiz.senderRefNrValidationPending.next(true))));

    this.subscriptions.push(this.deliveryForm.get('customerReferenceNumber').valueChanges.pipe(debounceTime(500)).subscribe(this.validateReceiverRefNr.bind(this)));
    this.subscriptions.push(this.deliveryForm.get('senderReferenceNumber').valueChanges.pipe(debounceTime(500)).subscribe(this.validateSenderRefNr.bind(this)));
  }

  protected getControlsConfig() {
    return {
      type: [this.deliveryMethod.type, Validators.required],
      from: [this.deliveryMethod.from, Validators.required],
      to: [this.deliveryMethod.to, Validators.required],
      clearing: [this.deliveryMethod.clearing],
      senderReferenceNumber: [this.deliveryMethod.senderReferenceNumber],
      customerReferenceNumber: [this.deliveryMethod.customerReferenceNumber],
      originalReferenceNumber: [this.deliveryMethod.originalReferenceNumber],
      transporter: [this.deliveryMethod.transporter],
      licensePlate: [this.deliveryMethod.licensePlate],
    };
  }

  validateSenderRefNr() {
    this.bothRefNrsValidated = false;
    let params = this.getRefNrValidationParams();
    this.validateRefNr({ ...params, poNumber: null, required: false, mask: null }, params.senderRefNumber).subscribe(result => {
      this.senderRefNrValidationPending.next(result.pending);
      if (!result.pending) {
        this.senderRefNrValidationErrorObject = result.error ? { [result.error]: true } : null;
        this.senderRefNrValidationWarningObject = result.warning ? { [result.warning]: true } : null;
      }
    });
  }

  validateReceiverRefNr() {
    this.bothRefNrsValidated = false;
    let params = this.getRefNrValidationParams();
    this.validateRefNr({ ...params, senderRefNumber: null }, params.poNumber).subscribe(result => {
      this.receiverRefNrValidationPending.next(result.pending);
      if (!result.pending) {
        this.receiverRefNrValidationErrorObject = result.error ? { [result.error]: true } : null;
        this.receiverRefNrValidationWarningObject = result.warning ? { [result.warning]: true } : null;
      }
    });
  }

  validateBothRefNrs(): Promise<boolean> {
    return new Promise<boolean>((resolve, reject) => {
      try {
        let params = this.getRefNrValidationParams();
        if (params.poNumber && params.senderRefNumber) {
          this.validateRefNr({ ...params }, params.poNumber).subscribe(result => {
            this.bothRefNrsValidationPending.next(result.pending);
            if (!result.pending) {
              this.senderRefNrValidationPending.next(false);
              this.receiverRefNrValidationPending.next(false);
              this.bothRefNrsValidated = !result.error;
              this.receiverRefNrValidationErrorObject = result.error ? { [result.error]: true } : null;
              this.receiverRefNrValidationWarningObject = result.warning ? { [result.warning]: true } : null;
              resolve(!result.error);
            }
          });
        } else {
          resolve(true);
        }
      } catch (e) {
        reject(e);
      }
    });
  }

  private getRefNrValidationParams(): RestCallParams {
    const to = this.getTo();
    const toId = to?.id;
    const fromId = this.deliveryForm.get('from').value?.id;
    const poNumberValidationInfo = (to?.poNumber) || { required: false, unique: null, mask: null, example: null };
    const senderRefNumber = this.deliveryForm.get('senderReferenceNumber').value;
    const poNumber = this.deliveryForm.get('customerReferenceNumber').value;
    return {
      ...poNumberValidationInfo,
      poNumber,
      fromId,
      toId,
      senderRefNumber,
      etmOrderNumber: this.getEtmOrderNumber(),
    };
  }

  private validateRefNr(params: RestCallParams, refNr: string): Observable<{ pending: boolean, warning?: string, error?: string }> {
    const subject = new BehaviorSubject<{ pending: boolean, warning?: string, error?: string }>({ pending: true });
    setTimeout(() => {
      if (refNr == null || refNr.trim() === '') { // no value
        if (params.required) {
          subject.next({ pending: false, error: this.translate.instant('ERRORS.ORDER.CREATE.RECEIVER_REF_IS_EMPTY') as string });
        } else {
          subject.next({ pending: false });
        }
        subject.complete();
      } else { // value filled in => always test (for special characters)
        this.poNumberAsyncValidator
          .doValidation(params)
          .pipe(first())
          .subscribe(response => {
            if (response.message) {
              if (response.success) {
                subject.next({ pending: false, warning: response.message });
              } else {
                this.formValidChanged.emit(false);
                subject.next({ pending: false, error: response.message });
              }
              subject.complete();
            } else if (response.success) {
              this.formValidChanged.emit(true);
              subject.next({ pending: false });
              subject.complete();
            }

          });
      }
    });
    return subject.asObservable();
  }

  protected setValidationOnPoReferenceNumber() {
    const to: any = this.getTo();
    const poNumberValidationInfo: ValidationParams = (to && to.poNumber) || { required: false, unique: false, mask: null, example: null };
    this.poNumberMandatory = poNumberValidationInfo && poNumberValidationInfo.required;
    this.poNumberMask = (to && to.poNumber && to.poNumber.example && this.translate.instant('ORDERS.DELIVERY.LABELS.MASK') + to.poNumber.example) || '';
    const customerReferenceNumberControl = this.deliveryForm.get('customerReferenceNumber');
    if (this.poNumberMandatory) {
      customerReferenceNumberControl.setValue(customerReferenceNumberControl.value);
    }
  }

  protected getETMShipmentNumbersAsString(shipmentNumbers: string[]): string {
    return shipmentNumbers && shipmentNumbers.join(', ');
  }

  protected abstract getEtmOrderNumber(): string;

  ngOnDestroy() {
    super.ngOnDestroy();
    this.subscriptions.forEach(subscription => subscription.unsubscribe());
  }

  protected abstract getTo(): CDTo;

  getName(definitions: Definition[], id: string): string {
    return util.getDefinitionName(definitions, id);
  }
}
